import { Col, Flex, Row, AutoComplete, AutoCompleteProps, Typography, Table, TableProps, FormInstance, Form } from 'antd';
import { useTranslation } from 'react-i18next';

import Icon from 'utils/icon';
import { isRTL } from 'core/services/language';
import { useState } from 'react';
import NotificationService from 'core/services/notification';
import { useDebouncedCallback } from 'use-debounce';
import { LoadingOutlined } from '@ant-design/icons';
import { onSearchDividendShareholder } from '../services/dividends';
import ListButton from 'components/listButton/listButton';
import NoData from 'components/noData/noData';
import dayjs from 'dayjs';
import { SearchDividendShareholderResponse } from '../models/dividends';
import InfoTooltip from 'components/info/info';
import { TranslatedField } from 'core/models/common';

interface AbsteensComponentProps {
	form: FormInstance;
	eligibility_date: string;
	revenue_per_share: string;
	viewOnly?: boolean;
	disabled?: boolean;
}

const AbsteensComponent = ({ form, eligibility_date, revenue_per_share, viewOnly, disabled }: AbsteensComponentProps) => {
	const { t } = useTranslation();

	const directTransferShareholdersWatcher = Form.useWatch('direct_transfer_excepted_shareholders', form) || [];
	const forbiddenShareholdersWatcher = Form.useWatch('forbidden_excepted_shareholders', form) || [];

	const [searchedShareholders, setSearchedShareholders] = useState<SearchDividendShareholderResponse['shareholders']>([]);
	const [searchedShareholdersOptions, setSearchedShareholdersOptions] = useState<AutoCompleteProps['options']>([]);
	const [isSearching, setIsSearching] = useState<boolean>(false);
	const [searchValue, setSearchValue] = useState<string>('');

	const columns: TableProps['columns'] = [
		{
			title: t('COMMON.ID'),
			dataIndex: 'id',
			key: 'id',
		},
		{
			title: t('COMMON.NAME'),
			dataIndex: 'name',
			key: 'name',
			render: (name: TranslatedField) => (isRTL() ? name.ar : name.en ?? name.ar),
		},
		{
			title: t('ACTIONS.PROFIT'),
			dataIndex: 'amount',
			key: 'amount',
		},
		{
			title: '',
			key: 'actions',
			render: (_, record) => <ListButton type="delete" onClick={() => onDeleteShareholder(record.id)} />,
		},
	];

	const searchShareholderHandler = useDebouncedCallback(async (text: string) => {
		try {
			setIsSearching(true);

			const response = await onSearchDividendShareholder(dayjs(eligibility_date).format('YYYY-MM-DD'), revenue_per_share, text);

			setSearchedShareholders(response.shareholders);

			setSearchedShareholdersOptions(
				response.shareholders
					.filter(
						(shareholder) =>
							!forbiddenShareholdersWatcher.some((forbidden: { id: number }) => forbidden.id === shareholder.id) &&
							!directTransferShareholdersWatcher.some((direct: { id: number }) => direct.id === shareholder.id),
					)
					.map((shareholder) => ({
						label: (
							<Flex align="center" gap={10}>
								<Icon color="var(--primary)" name="profile" />
								<p className="mb-0 py-1" style={{ fontSize: 15 }}>{`${
									isRTL() ? shareholder.name.ar : shareholder.name.en ?? shareholder.name.ar
								}`}</p>
							</Flex>
						),
						value: `${shareholder.id}`,
					})),
			);

			setIsSearching(false);
		} catch (error) {
			setIsSearching(false);
			NotificationService.error(t('COMMON.ERROR'), t('COMMON.FETCH_ERROR'));
		}
	}, 800);

	const selectShareholderHandler = (shareholderId: string) => {
		const shareholder = searchedShareholders.find((item) => item.id == Number(shareholderId));

		if (shareholder) {
			form.setFieldValue('forbidden_excepted_shareholders', [
				...forbiddenShareholdersWatcher,
				{ id: shareholder.id, name: shareholder.name, amount: shareholder.amount },
			]);
		}

		setSearchValue('');
		setSearchedShareholders([]);
		setSearchedShareholdersOptions([]);
	};

	const onDeleteShareholder = (shareholderId: number) => {
		const updatedShareholders = forbiddenShareholdersWatcher.filter((item: { id: number }) => item.id !== shareholderId);
		form.setFieldValue('forbidden_excepted_shareholders', updatedShareholders);
	};

	return (
		<Row gutter={[16, 16]} className="my-4">
			<Col span={24} md={8}>
				<Flex align="center" gap={5} className="mb-2">
					<Typography.Text style={{ fontSize: 15, marginBottom: 0, display: 'inline-block' }}>{t('ACTIONS.ABSTEEN_SHAREHOLDERS')}</Typography.Text>
					<InfoTooltip title={t('ACTIONS.ABSTEEN_SHAREHOLDERS_INFO')} />
				</Flex>

				<Form.Item hidden noStyle name="forbidden_excepted_shareholders" />
				<AutoComplete
					backfill
					value={searchValue}
					onChange={setSearchValue}
					style={{ width: '100%', height: 'auto' }}
					placeholder={t('ACTIONS.SEARCH_BY_NATIONAL_ID_OR_NAME')}
					prefix={<Icon name="search" style={{ marginInlineEnd: 10, marginTop: -3 }} />}
					options={searchedShareholdersOptions}
					onSelect={selectShareholderHandler}
					onSearch={searchShareholderHandler}
					disabled={viewOnly || disabled || !eligibility_date || !revenue_per_share}
					notFoundContent={
						isSearching ? (
							<span>
								<LoadingOutlined style={{ marginInlineEnd: 10, color: 'var(--primary)' }} /> {t('COMMON.SEARCHING')}
							</span>
						) : (
							t('COMMON.NO_DATA_FOUND')
						)
					}
				/>
			</Col>
			<Col span={24} md={16}>
				<Typography.Text style={{ fontSize: 15, marginBottom: 5, display: 'inline-block' }}>{t('ACTIONS.ABSTEENED_SHAREHOLDERS')}</Typography.Text>

				<Table
					columns={columns}
					dataSource={forbiddenShareholdersWatcher}
					bordered
					rowKey="id"
					pagination={false}
					locale={{ emptyText: <NoData message=" " /> }}
				/>
			</Col>
		</Row>
	);
};

export default AbsteensComponent;
